const STOPWORDS = new Set([
  'of',
  'the',
  'a',
  'an',
  'in',
  'on',
  'for',
  'to',
  'and',
  'with',
  'at',
  'by',
])

// A word cut back to a stem that is still a prefix of its other forms, since
// the terminology server matches each search word as a word prefix:
// "priorities" and "priority" both become "priorit". Conservative on
// purpose; a stem too short matches unrelated words.
function stem(word: string): string {
  if (word.endsWith('ies') && word.length > 4) {
    return word.slice(0, -3)
  }
  if (/(sses|xes|zes|ches|shes)$/.test(word)) {
    return word.slice(0, -2)
  }
  if (word.endsWith('s') && !word.endsWith('ss') && word.length > 3) {
    return word.slice(0, -1)
  }
  if (word.endsWith('ing') && word.length > 5) {
    return word.slice(0, -3)
  }
  if (word.endsWith('ed') && word.length > 4) {
    return word.slice(0, -2)
  }
  if (word.endsWith('y') && word.length > 4) {
    return word.slice(0, -1)
  }
  return word
}

// A word, keeping hyphenated words ("health-related") and codes ("67721-1")
// whole: the terminology server treats each as one word, and splitting them
// would send a stem ("relat") that starts no word it knows.
const WORD = /[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*/gu

// The words of a search as prefixes to match: lower case, without stopwords,
// letter-only words stemmed, hyphenated words and codes left as typed. Single
// characters are kept only when that is all there is.
export function searchTerms(input: string): string[] {
  const words = (input.toLowerCase().match(WORD) ?? []).filter(
    (word) => !STOPWORDS.has(word)
  )
  const meaningful = words.filter((word) => word.length >= 2)
  return (meaningful.length ? meaningful : words).map((word) =>
    /^\p{L}+$/u.test(word) ? stem(word) : word
  )
}

// The words of text searched in: as `searchTerms`, plus each part of a
// hyphenated word on its own, so "related" finds "health-related".
export function textTerms(text: string): string[] {
  const terms = searchTerms(text)
  return terms.flatMap((term) =>
    term.includes('-')
      ? [term, ...term.split('-').map((part) => stem(part))]
      : [term]
  )
}

// Whether every term of a search is the start of some term of the text.
export function matchesSearchTerms(search: string[], text: string[]): boolean {
  return search.every((term) => text.some((word) => word.startsWith(term)))
}
